import { EVERYONE, type HomeTemplateId, type HomeWidgetId } from '@obv/core';
import { create } from 'zustand';

export type SheetState =
  | { mode: 'add' }
  | { mode: 'edit'; planId: string }
  | { mode: 'addTask' }
  | { mode: 'editTask'; taskId: string }
  | { mode: 'addHabit' }
  | { mode: 'editHabit'; habitId: string }
  | { mode: 'addAlarm' }
  | { mode: 'editAlarm'; alarmId: string }
  | null;

export type CalendarView = 'day' | 'week' | 'month';

export interface HomeDraft {
  template: HomeTemplateId;
  layout: HomeWidgetId[];
}

interface DisplayUiState {
  filter: string;
  /** Offset in days from today for the Calendar's anchor date (day, week or month in view). */
  dayOffset: number;
  calView: CalendarView;
  sheet: SheetState;
  /** Where the Quran page should open (set by the verse widget); consumed on open. */
  quranTarget: { surah: number; ayah: number } | null;
  /** Kanji the Japanese page should open on (set by the slideshow widget). */
  kanjiTarget: number | null;
  openQuranAt: (t: { surah: number; ayah: number } | null) => void;
  openKanji: (id: number | null) => void;
  /** Draft while the home screen is being customized; null when not editing. */
  homeDraft: HomeDraft | null;
  startHomeEdit: (draft: HomeDraft) => void;
  setHomeDraft: (draft: HomeDraft) => void;
  endHomeEdit: () => void;
  setFilter: (f: string) => void;
  shiftDay: (n: number) => void;
  setDayOffset: (n: number) => void;
  resetDay: () => void;
  setCalView: (v: CalendarView) => void;
  openSheet: (s: Exclude<SheetState, null>) => void;
  closeSheet: () => void;
}

export const MIN_OFFSET = -30;
export const MAX_OFFSET = 90;
const clampOffset = (n: number) => Math.max(MIN_OFFSET, Math.min(MAX_OFFSET, n));

export const useDisplayUi = create<DisplayUiState>((set) => ({
  filter: EVERYONE,
  dayOffset: 0,
  calView: 'day',
  sheet: null,
  homeDraft: null,
  quranTarget: null,
  kanjiTarget: null,
  openQuranAt: (quranTarget) => set({ quranTarget }),
  openKanji: (kanjiTarget) => set({ kanjiTarget }),
  startHomeEdit: (draft) => set({ homeDraft: { template: draft.template, layout: draft.layout.slice() } }),
  setHomeDraft: (homeDraft) => set({ homeDraft }),
  endHomeEdit: () => set({ homeDraft: null }),
  setFilter: (filter) => set({ filter }),
  shiftDay: (n) => set((s) => ({ dayOffset: clampOffset(s.dayOffset + n) })),
  setDayOffset: (n) => set({ dayOffset: clampOffset(n) }),
  resetDay: () => set({ dayOffset: 0 }),
  setCalView: (calView) => set({ calView }),
  openSheet: (sheet) => set({ sheet }),
  closeSheet: () => set({ sheet: null }),
}));
